/**
 * Entity storage (TRD-01 §5).
 *
 * Structure-of-arrays is the reflexive answer for a simulation, and it is premature here:
 * 400 enemies × 100 towers at 60 Hz is nowhere near a bottleneck, and SoA makes every
 * gameplay system harder to read and to get right. Typed objects in insertion-ordered
 * arrays, pooled to avoid per-tick allocation, is the correct trade at this scale.
 *
 * Two rules the whole determinism contract leans on:
 *  - Iteration is by index over an insertion-ordered array (TRD-00 R-00-05).
 *  - Dead entities are tombstoned and swept at end of tick, never spliced mid-iteration
 *    (TRD-01 R-01-08). Splicing while a system iterates is the classic "works until a
 *    Venom burst and a Void execute land on the same tick" bug.
 */
import { createStatusState, resetStatusState, type StatusState } from './statuses.js';
import { createTowerMods, resetTowerMods, type TowerMods } from './mods.js';
import {
  NO_TARGET,
  type DamageKind,
  type EnemyId,
  type EntityId,
  type ProjectileKind,
  type StarLevel,
  type TargetingMode,
  type TowerId,
} from './types.js';

export interface Entity {
  id: EntityId;
  alive: boolean;
}

export interface Enemy extends Entity {
  archetype: EnemyId;
  /** Tile units, never pixels (TRD-01 R-01-10). */
  x: number;
  y: number;
  /** Distance travelled along the path, in tiles. THE authority on position. */
  pathT: number;
  pathId: number;
  speed: number;
  baseSpeed: number;
  hp: number;
  maxHp: number;
  armor: number;
  baseArmor: number;
  size: number;
  leakDamage: number;
  bounty: number;
  flying: boolean;
  /** Flat absorption subtracted at damage stage 7 (TRD-06 R-06-15). Zero for most. */
  shieldPerHit: number;
  /** The breakable shield BAR — see `EnemyArchetype.shieldHp`. `maxShieldHp` of 0 means
   *  this enemy has no shield phase at all, which is every enemy but Prism. */
  shieldHp: number;
  maxShieldHp: number;
  /** How long the enemy stays down once the bar empties. Authored, never changes. */
  shieldDownTicks: number;
  /** Absolute tick the shield comes back. 0 = shield is UP (or there is none). While
   *  this is in the future the enemy is frozen and its health is reachable. */
  shieldDownUntilTick: number;
  /** How many splits produced this enemy. Capped so cascades cannot run away. */
  splitDepth: number;
  /** `leaper` movement (B-16): every N ticks of age, jump `leapTiles` forward. 0 = never. */
  leapEveryTicks: number;
  leapTiles: number;
  /** Bitless set of statuses that bounce off this enemy entirely. */
  immuneStun: boolean;
  immuneFreeze: boolean;
  /**
   * Tick until which stuns and freezes bounce off — the hard-CC recovery window
   * (`HARD_CC_RECOVERY_TICKS`). Set when an immobilise lands, never authored.
   */
  ccImmuneUntilTick: number;
  /** Absolute tick this enemy finishes rising out of the ground. Immobile and
   *  untouchable until then — see `EnemyArchetype.emergeTicks`. */
  emergeUntilTick: number;
  /** Every N ticks of age, blink to the mirrored half of the map. 0 = never. */
  teleportEveryTicks: number;
  /**
   * Disabler (playtest request): the shot's reach, its blast radius, how long the towers
   * it catches stay dark, and its cadence. All zero for everything else.
   */
  disableRange: number;
  disableRadius: number;
  disableTicks: number;
  disableEveryTicks: number;
  /** Absolute tick the next shot is allowed. */
  disableReadyTick: number;
  /**
   * Which way it is travelling, as a raw (unnormalised) delta. Used only by the frontal
   * plate below, which compares directions with a dot product and therefore never needs
   * this to be a unit vector.
   *
   * Persists when the enemy is not moving — a frozen enemy still faces where it was
   * going, so a stun does not silently strip its armour.
   */
  facingX: number;
  facingY: number;
  /**
   * The frontal plate (playtest request). `frontalCos` is the COSINE of the half-angle it
   * covers, so the arc test is a dot product and needs no trigonometry — the determinism
   * contract forbids `cos`/`atan2` here, and a cosine authored as data cannot drift
   * between two engines the way a computed one could.
   *
   * 0 means no plate, which is every enemy but one.
   */
  frontalCos: number;
  frontalReduction: number;
  statuses: StatusState;
  spawnTick: number;
  /**
   * The tower that landed the killing blow, so system 10 can run ITS on-kill hooks.
   * Deaths are marked at damage time and resolved a system later (TRD-05 R-05-20), so
   * the attribution has to survive the gap.
   */
  lastHitBy: EntityId;
}

/**
 * The stats a tower needs to fight, copied off its `TowerDef` when it is placed.
 *
 * The sim cannot import `@prism/content` — TRD-00 §4.1 keeps its dependencies empty and
 * `check-sim-boundary` enforces it, while TRD-02 R-02-01 has content importing sim types.
 * So definitions reach the simulation the same way an enemy archetype's stats already do:
 * copied onto the entity at spawn. `Enemy` carries `hp`/`armor`/`size`/`bounty` for
 * exactly this reason, and a tower is no different.
 *
 * TRD-07 owns placement and is what fills these in. A tower placed without them has
 * `damage: 0` and `attackInterval: 0`, and will never fire.
 */
export interface TowerCombatStats {
  damage: number;
  damageKind: DamageKind;
  /** Seconds. Converted to whole ticks at fire time (TRD-05 R-05-09). */
  attackInterval: number;
  /** Tiles. */
  range: number;
  projectileKind: ProjectileKind;
  /** Tiles per second. Zero only for beams. */
  projectileSpeed: number;
  projectileRadius: number;
  /** Radians per second; homing only. */
  turnRate: number;
  /** Tiles. Zero means single-target. */
  aoeRadius: number;

  // ── Firing approach (TRD-05 §5.1) ──────────────────────────────────────────────────
  // These are what separate thirty towers that all shoot one dot from thirty towers that
  // feel different. Each is independent of `projectileKind`, so a salvo of homing
  // missiles and a piercing lob are both expressible without a new kind.

  /**
   * Projectiles per shot, fired as a symmetric fan of `spreadRadians` (R-05-28).
   *
   * Damage is DIVIDED across the fan, so a salvo that lands entirely on one big target
   * deals the tower's full damage and one that clips a straggler deals a fraction. That
   * is the whole design of a shotgun, and it falls out of the division for free.
   */
  salvo: number;
  /** Total fan angle in radians. Meaningless when `salvo` is 1. */
  spreadRadians: number;
  /** Shots fired back-to-back before the full cooldown applies (R-05-29). 1 = no burst. */
  burstCount: number;
  /** Ticks between the shots of a burst. */
  burstIntervalTicks: number;
  /** Enemies a travelling projectile passes through before dying (R-05-30). */
  pierce: number;
  /** Tiles/s², added along the heading each tick. Non-zero makes a missile (R-05-31). */
  projectileAcceleration: number;
  /**
   * `laser` only (R-05-32): damage multiplier gained per consecutive tick held on the
   * SAME enemy, capped at `beamRampMax`. Zero makes a laser a flat-damage channel.
   */
  beamRampPerTick: number;
  beamRampMax: number;

  // ── Zone weapon (backlog B-18/B-19) ─────────────────────────────────────────────────
  // Only read when `projectileKind` is 'zone'. The cast anchors an area of `aoeRadius`
  // at the target: `zoneWindupTicks` of telegraph, then a strike every
  // `zoneStrikeEveryTicks` for `zoneActiveTicks`. A non-zero `zoneTrackSpeed` makes the
  // area drift after its mark (B-19) — the searchlight the enemy tries to outrun.
  zoneWindupTicks: number;
  zoneActiveTicks: number;
  zoneStrikeEveryTicks: number;
  /** Tiles per second. Zero = the zone stays where it was cast. */
  zoneTrackSpeed: number;

  /** Index 0 is always 1 — a 1-star tower is the baseline (TRD-02 R-02-07). */
  starScaling: readonly [1, number, number];
  /** Range's own star curve — see `TowerSpec.rangeScaling`. */
  rangeScaling: readonly [1, number, number];
  /**
   * The support surge (playtest request), kept entirely in WAVE numbers.
   *
   * `surgeWave` is the wave the surge is live for — set to the NEXT one when armed during
   * a build phase, so a player who can see what is coming may spend it before the round
   * starts and have it carry in. `surgeReadyWave` is the first wave it may be spent on
   * again.
   *
   * Two integers rather than a flag and a timer because every question the UI asks is
   * then arithmetic on the current wave — is it live, is it armed, how many rounds until
   * it returns — with nothing to reset and nothing to tick down.
   */
  surgeWave: number;
  surgeReadyWave: number;
  /**
   * Knocked out by a Disabler until this tick. A disabled tower holds its target and its
   * cooldown but does not fire, so coming back is instant rather than a re-acquisition.
   */
  disabledUntilTick: number;
  /** Cannot be disabled again before this tick — see `TOWER_DISABLE_RECOVERY_TICKS`. */
  disableImmuneUntilTick: number;
}

export interface Tower extends Entity, TowerCombatStats {
  defId: TowerId;
  tileX: number;
  tileY: number;
  /** Centre in tile space: tileX + 0.5 (TRD-07 R-07-13). Cached to avoid recomputing. */
  x: number;
  y: number;
  star: StarLevel;
  /** Ticks until it can fire again. Integer — never accumulate fractions (TRD-05 R-05-09). */
  cooldown: number;
  targetId: EntityId;
  targetingMode: TargetingMode;
  /** Persists across waves; read by "every Nth shot" synergies (TRD-05 R-05-11). */
  shotsFired: number;
  /** Shots left in the current burst. Zero means the next shot starts a fresh one. */
  burstRemaining: number;
  /**
   * Consecutive `laser` damage ticks landed on `beamTargetId`. Reset the moment the beam
   * moves (R-05-32) — the ramp is a reward for holding a target, so switching has to cost
   * it. Part of the tower hash: two runs that ramp differently are two different runs.
   */
  beamStacks: number;
  beamTargetId: EntityId;
  /** Gold sunk in, including merged components, so sell refunds stay fair. */
  investedGold: number;
  /** Surfaced in the run summary — the most requested stat in any TD (TRD-09 R-09-26). */
  damageDealt: number;
  /**
   * Damage dealt by towers THIS tower was buffing when the hit landed (backlog B-12
   * stretch) — the aura carrier's answer to "what did my Signal Lens actually do".
   * Presentation-only, like `damageDealt`: never hashed, never read by a rule.
   */
  assistDealt: number;
  /**
   * The aura carriers currently buffing this tower, rebuilt by every synergy recompute
   * (so it can never outlive a sell or merge). Transient bookkeeping for `assistDealt`.
   */
  assistSources: Tower[];
  /** Recomputed from scratch by the synergy system; never hand-edited (TRD-08 R-08-05). */
  mods: TowerMods;
}

export interface Projectile extends Entity {
  x: number;
  y: number;
  vx: number;
  vy: number;
  sourceTower: EntityId;
  targetId: EntityId;
  damage: number;
  kind: DamageKind;
  crit: boolean;
  pierceRemaining: number;
  chainRemaining: number;
  /** Ticks remaining before it expires (TRD-05 R-05-15). */
  lifetime: number;
  radius: number;
  turnRate: number;
  /** Tiles/s² along the current heading, capped at `MISSILE_MAX_SPEED_FACTOR` × launch. */
  accel: number;
  /** Launch speed, kept so acceleration has a ceiling to clamp against. */
  launchSpeed: number;
  /** Tiles. Non-zero makes every impact an area hit (TRD-05 R-05-17). */
  aoeRadius: number;
  /**
   * True only for `lob`: detonate on arrival at the snapshotted ground point, hit or
   * miss. A homing missile carries an AoE too but must NOT do this — its ground snapshot
   * is stale the moment its target moves, and it would burst in mid-air behind the enemy.
   */
  detonateAtGround: boolean;
  /** Ground position a `lob` was aimed at, snapshotted at fire time (TRD-05 R-05-13). */
  targetX: number;
  targetY: number;
  /**
   * Enemies this projectile has already hit, so a piercing shot cannot hit the same
   * enemy twice as it passes through. Cleared in place on reuse, never reallocated.
   */
  readonly hitIds: EntityId[];
}

export interface Drone extends Entity {
  ownerTower: EntityId;
  x: number;
  y: number;
  damage: number;
  expiresAtTick: number;
  targetId: EntityId;
  orbitAngle: number;
}

/**
 * A timed area attack (backlog B-18/B-19) — a fourth entity type for the same reason
 * drones are one: zones neither move like projectiles nor live like towers. Damage
 * is rolled ONCE at cast (like an AoE shot) and applied per strike through the normal
 * pipeline, so a zone kill tombstones and resolves exactly like any other.
 */
export interface Zone extends Entity {
  ownerTower: EntityId;
  x: number;
  y: number;
  /** Tiles — copied from the caster's `aoeRadius` at cast time. */
  radius: number;
  /** Per strike, rolled at cast. */
  damage: number;
  kind: DamageKind;
  crit: boolean;
  /** Telegraph ticks remaining; the first strike lands when this reaches zero. */
  windupLeft: number;
  strikeEveryTicks: number;
  /** Active ticks remaining once the windup has elapsed. */
  ticksLeft: number;
  /** Ticks since the last strike — drives the cadence, readable by the renderer. */
  sinceStrike: number;
  /** Tiles per second toward `targetId`. Zero = static (B-18); non-zero tracks (B-19). */
  trackSpeed: number;
  targetId: EntityId;
}

/**
 * An insertion-ordered pool.
 *
 * `spawn` reuses a dead object's memory but ALWAYS assigns a fresh id — ids are never
 * recycled (TRD-01 R-01-09). Recycling them produces visual entity-identity bugs in the
 * renderer that look like renderer bugs and are not: a damage number follows a corpse, a
 * health bar snaps to a new enemy across the map.
 */
export class EntityPool<T extends Entity> {
  /** Live and tombstoned, in insertion order. Systems iterate this by index. */
  readonly items: T[] = [];

  private readonly index = new Map<EntityId, T>();
  private readonly free: T[] = [];

  constructor(
    private readonly create: () => T,
    private readonly reset: (entity: T) => void,
  ) {}

  spawn(id: EntityId): T {
    const entity = this.free.pop() ?? this.create();
    this.reset(entity);
    entity.id = id;
    entity.alive = true;
    this.items.push(entity);
    this.index.set(id, entity);
    return entity;
  }

  get(id: EntityId): T | undefined {
    const entity = this.index.get(id);
    return entity !== undefined && entity.alive ? entity : undefined;
  }

  /** Tombstone. The object stays in `items` until the next compaction. */
  kill(entity: T): void {
    entity.alive = false;
  }

  killById(id: EntityId): boolean {
    const entity = this.get(id);
    if (!entity) return false;
    entity.alive = false;
    return true;
  }

  /** Sweep tombstones, preserving the relative order of survivors (TRD-01 R-01-08). */
  compact(): void {
    let write = 0;
    for (let read = 0; read < this.items.length; read++) {
      const entity = this.items[read] as T;
      if (entity.alive) {
        this.items[write++] = entity;
      } else {
        this.index.delete(entity.id);
        this.free.push(entity);
      }
    }
    this.items.length = write;
  }

  /** Live count. O(n) — call it for UI, not inside a hot loop. */
  countAlive(): number {
    let n = 0;
    for (let i = 0; i < this.items.length; i++) {
      if (this.items[i]?.alive) n++;
    }
    return n;
  }

  clear(): void {
    for (const entity of this.items) {
      entity.alive = false;
      this.free.push(entity);
    }
    this.items.length = 0;
    this.index.clear();
  }

  /** Pooled-but-unused objects, for leak diagnostics in the debug overlay. */
  get pooledCount(): number {
    return this.free.length;
  }
}

// ── Factories ─────────────────────────────────────────────────────────────────────────

/** Inert stats. A tower holding these cannot fire, which is what an unplaced one wants. */
export function createTowerCombatStats(): TowerCombatStats {
  return {
    damage: 0,
    damageKind: 'physical',
    attackInterval: 0,
    range: 0,
    projectileKind: 'bullet',
    projectileSpeed: 0,
    projectileRadius: 0.1,
    turnRate: 0,
    aoeRadius: 0,
    salvo: 1,
    spreadRadians: 0,
    burstCount: 1,
    burstIntervalTicks: 0,
    pierce: 0,
    projectileAcceleration: 0,
    beamRampPerTick: 0,
    beamRampMax: 1,
    zoneWindupTicks: 0,
    zoneActiveTicks: 0,
    zoneStrikeEveryTicks: 0,
    zoneTrackSpeed: 0,
    starScaling: STAR_SCALING_IDENTITY,
    rangeScaling: STAR_SCALING_IDENTITY,
    surgeWave: 0,
    surgeReadyWave: 0,
    disabledUntilTick: 0,
    disableImmuneUntilTick: 0,
  };
}

/** Shared so pool reset does not allocate a tuple per tower (TRD-01 R-01-12). */
const STAR_SCALING_IDENTITY: readonly [1, number, number] = [1, 1, 1];

export function resetTowerCombatStats(t: TowerCombatStats): void {
  t.damage = 0;
  t.damageKind = 'physical';
  t.attackInterval = 0;
  t.range = 0;
  t.projectileKind = 'bullet';
  t.projectileSpeed = 0;
  t.projectileRadius = 0.1;
  t.turnRate = 0;
  t.aoeRadius = 0;
  t.salvo = 1;
  t.spreadRadians = 0;
  t.burstCount = 1;
  t.burstIntervalTicks = 0;
  t.pierce = 0;
  t.projectileAcceleration = 0;
  t.beamRampPerTick = 0;
  t.beamRampMax = 1;
  t.zoneWindupTicks = 0;
  t.zoneActiveTicks = 0;
  t.zoneStrikeEveryTicks = 0;
  t.zoneTrackSpeed = 0;
  t.starScaling = STAR_SCALING_IDENTITY;
  t.rangeScaling = STAR_SCALING_IDENTITY;
  t.surgeWave = 0;
  t.surgeReadyWave = 0;
  t.disabledUntilTick = 0;
  t.disableImmuneUntilTick = 0;
}

export function createEnemyPool(): EntityPool<Enemy> {
  return new EntityPool<Enemy>(
    () => ({
      id: 0,
      alive: false,
      archetype: '',
      x: 0,
      y: 0,
      pathT: 0,
      pathId: 0,
      speed: 0,
      baseSpeed: 0,
      hp: 0,
      maxHp: 0,
      armor: 0,
      baseArmor: 0,
      size: 0.3,
      leakDamage: 1,
      bounty: 0,
      flying: false,
      shieldPerHit: 0,
      shieldHp: 0,
      maxShieldHp: 0,
      shieldDownTicks: 0,
      shieldDownUntilTick: 0,
      splitDepth: 0,
      leapEveryTicks: 0,
      leapTiles: 0,
      immuneStun: false,
      immuneFreeze: false,
      ccImmuneUntilTick: 0,
      emergeUntilTick: 0,
      teleportEveryTicks: 0,
      disableRange: 0,
      disableRadius: 0,
      disableTicks: 0,
      disableEveryTicks: 0,
      disableReadyTick: 0,
      facingX: 1,
      facingY: 0,
      frontalCos: 0,
      frontalReduction: 0,
      statuses: createStatusState(),
      spawnTick: 0,
      lastHitBy: NO_TARGET,
    }),
    (e) => {
      e.archetype = '';
      e.x = 0;
      e.y = 0;
      e.pathT = 0;
      e.pathId = 0;
      e.speed = 0;
      e.baseSpeed = 0;
      e.hp = 0;
      e.maxHp = 0;
      e.armor = 0;
      e.baseArmor = 0;
      e.size = 0.3;
      e.leakDamage = 1;
      e.bounty = 0;
      e.flying = false;
      e.shieldPerHit = 0;
      e.shieldHp = 0;
      e.maxShieldHp = 0;
      e.shieldDownTicks = 0;
      e.shieldDownUntilTick = 0;
      e.splitDepth = 0;
      e.leapEveryTicks = 0;
      e.leapTiles = 0;
      e.immuneStun = false;
      e.immuneFreeze = false;
      e.ccImmuneUntilTick = 0;
      e.emergeUntilTick = 0;
      e.teleportEveryTicks = 0;
      e.disableRange = 0;
      e.disableRadius = 0;
      e.disableTicks = 0;
      e.disableEveryTicks = 0;
      e.disableReadyTick = 0;
      // Facing right: every spawn is on the left edge walking in.
      e.facingX = 1;
      e.facingY = 0;
      e.frontalCos = 0;
      e.frontalReduction = 0;
      e.spawnTick = 0;
      e.lastHitBy = NO_TARGET;
      resetStatusState(e.statuses);
    },
  );
}

export function createTowerPool(): EntityPool<Tower> {
  return new EntityPool<Tower>(
    () => ({
      id: 0,
      alive: false,
      defId: '',
      tileX: 0,
      tileY: 0,
      x: 0,
      y: 0,
      star: 1,
      cooldown: 0,
      targetId: NO_TARGET,
      targetingMode: 'first',
      shotsFired: 0,
      burstRemaining: 0,
      beamStacks: 0,
      beamTargetId: NO_TARGET,
      investedGold: 0,
      damageDealt: 0,
      assistDealt: 0,
      assistSources: [],
      mods: createTowerMods(),
      ...createTowerCombatStats(),
    }),
    (t) => {
      t.defId = '';
      t.tileX = 0;
      t.tileY = 0;
      t.x = 0;
      t.y = 0;
      t.star = 1;
      t.cooldown = 0;
      t.targetId = NO_TARGET;
      t.targetingMode = 'first';
      t.shotsFired = 0;
      t.burstRemaining = 0;
      t.beamStacks = 0;
      t.beamTargetId = NO_TARGET;
      t.investedGold = 0;
      t.damageDealt = 0;
      t.assistDealt = 0;
      t.assistSources.length = 0;
      resetTowerMods(t.mods);
      resetTowerCombatStats(t);
    },
  );
}

export function createProjectilePool(): EntityPool<Projectile> {
  return new EntityPool<Projectile>(
    () => ({
      id: 0,
      alive: false,
      x: 0,
      y: 0,
      vx: 0,
      vy: 0,
      sourceTower: NO_TARGET,
      targetId: NO_TARGET,
      damage: 0,
      kind: 'physical',
      crit: false,
      pierceRemaining: 0,
      chainRemaining: 0,
      lifetime: 0,
      radius: 0.1,
      turnRate: 0,
      accel: 0,
      launchSpeed: 0,
      aoeRadius: 0,
      detonateAtGround: false,
      targetX: 0,
      targetY: 0,
      hitIds: [],
    }),
    (p) => {
      p.x = 0;
      p.y = 0;
      p.vx = 0;
      p.vy = 0;
      p.sourceTower = NO_TARGET;
      p.targetId = NO_TARGET;
      p.damage = 0;
      p.kind = 'physical';
      p.crit = false;
      p.pierceRemaining = 0;
      p.chainRemaining = 0;
      p.lifetime = 0;
      p.radius = 0.1;
      p.turnRate = 0;
      p.accel = 0;
      p.launchSpeed = 0;
      p.aoeRadius = 0;
      p.detonateAtGround = false;
      p.targetX = 0;
      p.targetY = 0;
      p.hitIds.length = 0;
    },
  );
}

export function createZonePool(): EntityPool<Zone> {
  return new EntityPool<Zone>(
    () => ({
      id: 0,
      alive: false,
      ownerTower: NO_TARGET,
      x: 0,
      y: 0,
      radius: 0,
      damage: 0,
      kind: 'physical',
      crit: false,
      windupLeft: 0,
      strikeEveryTicks: 1,
      ticksLeft: 0,
      sinceStrike: 0,
      trackSpeed: 0,
      targetId: NO_TARGET,
    }),
    (z) => {
      z.ownerTower = NO_TARGET;
      z.x = 0;
      z.y = 0;
      z.radius = 0;
      z.damage = 0;
      z.kind = 'physical';
      z.crit = false;
      z.windupLeft = 0;
      z.strikeEveryTicks = 1;
      z.ticksLeft = 0;
      z.sinceStrike = 0;
      z.trackSpeed = 0;
      z.targetId = NO_TARGET;
    },
  );
}

export function createDronePool(): EntityPool<Drone> {
  return new EntityPool<Drone>(
    () => ({
      id: 0,
      alive: false,
      ownerTower: NO_TARGET,
      x: 0,
      y: 0,
      damage: 0,
      expiresAtTick: 0,
      targetId: NO_TARGET,
      orbitAngle: 0,
    }),
    (d) => {
      d.ownerTower = NO_TARGET;
      d.x = 0;
      d.y = 0;
      d.damage = 0;
      d.expiresAtTick = 0;
      d.targetId = NO_TARGET;
      d.orbitAngle = 0;
    },
  );
}
